import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  HiOutlineTrophy,
  HiOutlineChartBar,
  HiOutlineBookOpen,
  HiOutlineStar,
  HiOutlineArrowTrendingUp,
  HiOutlineUserGroup,
} from "react-icons/hi2";
import api from "../../../../services/api";

export default function Stats() {
  const user = useSelector((state) => state.Auth.user);
  const navigate = useNavigate();
  const [myStats, setMyStats] = useState(null);
  const [topAllTime, setTopAllTime] = useState([]);
  const [topMonthly, setTopMonthly] = useState([]);
  const [recentCompletions, setRecentCompletions] = useState([]);
  const [workshopStats, setWorkshopStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAll();
  }, []);

  async function fetchAll() {
    setLoading(true);
    try {
      const [contribRes, allTimeRes, monthlyRes, recentRes, workshopRes] = await Promise.all([
        api.post("/workshop_user/contribution"),
        api.post("/workshop_user/top_contributors", { period: "alltime" }),
        api.post("/workshop_user/top_contributors", { period: "month" }),
        api.post("/workshop_user/search", { per_page: 15, page: 1 }),
        api.post("/workshop/search", { perPage: 1000, short: true }),
      ]);

      setMyStats(contribRes.data || {});
      setTopAllTime(allTimeRes.data || []);
      setTopMonthly(monthlyRes.data || []);
      setRecentCompletions(recentRes.data || []);

      // Calculate workshop-level stats
      const workshops = workshopRes.data || [];
      const withRating = workshops.filter((w) => w.rating_score);
      const avgRating = withRating.length > 0 ? withRating.reduce((sum, w) => sum + parseFloat(w.rating_score), 0) / withRating.length : 0;

      setWorkshopStats({
        total: workshops.length,
        withRatings: withRating.length,
        avgRating: avgRating.toFixed(1),
        topRated: [...withRating].sort((a, b) => parseFloat(b.rating_score) - parseFloat(a.rating_score)).slice(0, 5),
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  const completionRate = myStats?.workshops && myStats?.user_workshops ? Math.round((myStats.user_workshops / myStats.workshops) * 100) : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Statistics & Insights</h1>
          <p className="text-gray-600">Track your learning progress and see how the community is doing</p>
        </div>

        {/* My Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <HiOutlineTrophy className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-sm font-medium text-gray-500">My Score</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">{myStats?.score || 0}</div>
            <div className="text-xs text-gray-500 mt-1">Total contribution points</div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <HiOutlineBookOpen className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-sm font-medium text-gray-500">Completed</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">{myStats?.user_workshops || 0}</div>
            <div className="text-xs text-gray-500 mt-1">of {myStats?.workshops || 0} workshops</div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                <HiOutlineArrowTrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-sm font-medium text-gray-500">Completion Rate</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">{completionRate}%</div>
            <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
              <div className="h-2 rounded-full bg-purple-500 transition-all duration-500" style={{ width: `${Math.min(completionRate, 100)}%` }} />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <HiOutlineStar className="w-5 h-5 text-amber-600" />
              </div>
              <span className="text-sm font-medium text-gray-500">Avg Workshop Rating</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">{workshopStats?.avgRating || "—"}</div>
            <div className="text-xs text-gray-500 mt-1">{workshopStats?.withRatings || 0} rated workshops</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Top-Rated Workshops */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-9 h-9 bg-amber-100 rounded-xl flex items-center justify-center">
                <HiOutlineStar className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Top-Rated Workshops</h3>
            </div>
            <div className="space-y-3">
              {workshopStats?.topRated?.length > 0 ? (
                workshopStats.topRated.map((w, i) => (
                  <div
                    key={w._id}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/selecole/learn/${w._id}`)}>
                    <span className="text-sm font-bold text-gray-400 w-5">{i + 1}</span>
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                      <img src={w.image || "/placeholder.png"} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{w.name}</p>
                    </div>
                    <span className="flex items-center gap-1 text-sm font-semibold text-amber-600">
                      <HiOutlineStar className="w-4 h-4" />
                      {w.rating_score}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">No rated workshops yet</p>
              )}
            </div>
          </div>

          {/* Monthly Leaderboard */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-9 h-9 bg-green-100 rounded-xl flex items-center justify-center">
                <HiOutlineChartBar className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Monthly Leaderboard</h3>
                <p className="text-xs text-gray-500">Last 30 days</p>
              </div>
            </div>
            <div className="space-y-2">
              {topMonthly.length > 0 ? (
                topMonthly.slice(0, 10).map((contributor, i) => (
                  <div key={contributor._id || i} className="flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-gray-50 transition-colors">
                    <span className={`text-sm font-bold w-5 text-center ${i < 3 ? "text-amber-500" : "text-gray-400"}`}>{i + 1}</span>
                    <img src={contributor.user_avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{contributor.user_name}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-semibold text-gray-900">{contributor.sumFeedbackRating}</div>
                      <div className="text-xs text-gray-400">{contributor.countWorkshops} wk</div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">No activity this month</p>
              )}
            </div>
          </div>

          {/* All-Time Leaderboard */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-9 h-9 bg-purple-100 rounded-xl flex items-center justify-center">
                <HiOutlineUserGroup className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">All-Time Leaderboard</h3>
                <p className="text-xs text-gray-500">Since the beginning</p>
              </div>
            </div>
            <div className="space-y-2">
              {topAllTime.length > 0 ? (
                topAllTime.slice(0, 10).map((contributor, i) => (
                  <div key={contributor._id || i} className="flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-gray-50 transition-colors">
                    <span className={`text-sm font-bold w-5 text-center ${i < 3 ? "text-amber-500" : "text-gray-400"}`}>{i + 1}</span>
                    <img src={contributor.user_avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{contributor.user_name}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-semibold text-gray-900">{contributor.sumFeedbackRating}</div>
                      <div className="text-xs text-gray-400">{contributor.countWorkshops} wk</div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">No data yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Recent Completions */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-blue-100 rounded-xl flex items-center justify-center">
                <HiOutlineBookOpen className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Recent Completions</h3>
                <p className="text-xs text-gray-500">Latest workshop activity across the team</p>
              </div>
            </div>
          </div>

          {recentCompletions.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No completions recorded yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-3">Person</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-3">Workshop</th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-3">Rating</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-3">Comment</th>
                    <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-3">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {recentCompletions.map((item) => (
                    <tr
                      key={item._id}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/selecole/learn/${item.workshop_id}`)}>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2">
                          <img src={item.user_avatar} alt="" className="w-7 h-7 rounded-full object-cover" />
                          <span className="text-sm font-medium text-gray-900">{item.user_name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <span className="text-sm text-gray-700 truncate block max-w-[200px]">{item.workshop_name}</span>
                      </td>
                      <td className="py-3 px-3 text-center">
                        {item.feedback_rating ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                            <HiOutlineStar className="w-3 h-3" />
                            {item.feedback_rating}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </td>
                      <td className="py-3 px-3">
                        <span className="text-xs text-gray-500 truncate block max-w-[200px]">{item.feedback_text || "—"}</span>
                      </td>
                      <td className="py-3 px-3 text-right">
                        <span className="text-xs text-gray-400">{item.created_at ? new Date(item.created_at).toLocaleDateString() : ""}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
